/**
 * 上行：48 kHz 立体声 s16le（Worklet）→ mono f32 → WebCodecs AudioEncoder(opus) → 裸 Opus 帧（每帧一次 MQTT）。
 */

import {
  OPUS_UPLINK_BITRATE_BPS,
  OPUS_UPLINK_FRAME_SAMPLES,
  OPUS_UPLINK_SAMPLE_RATE,
  getOpusUplinkInputGain,
} from '../../constants/Codec2Constants';
import { buildOpusHeadMono48k } from './opusHead';

function stereoS16leToMonoF32(ab: ArrayBuffer, gain: number): Float32Array {
  if (ab.byteLength < 4) {
    return new Float32Array(0);
  }
  const frames = Math.floor(ab.byteLength / 4);
  const i16 = new Int16Array(ab, 0, frames * 2);
  const out = new Float32Array(frames);
  const sc = 1 / 32768;
  const g = gain;
  for (let i = 0; i < frames; i++) {
    const l = i16[i * 2];
    const r = i16[i * 2 + 1];
    let v = (l + r) * 0.5 * sc * g;
    if (v > 1) {
      v = 1;
    } else if (v < -1) {
      v = -1;
    }
    out[i] = v;
  }
  return out;
}

export class OpusWebCodecsUpstreamEncoder {
  private encoder: AudioEncoder | null = null;
  private configured = false;
  private timestampUs = 0;
  private remainder = new Float32Array(0);
  private closed = false;

  constructor(private readonly onEncoded: (opusPacket: ArrayBuffer) => void | Promise<void>) {}

  static isSupported(): boolean {
    return typeof AudioEncoder !== 'undefined' && typeof AudioData !== 'undefined';
  }

  async ensureConfigured(): Promise<boolean> {
    if (this.closed) {
      return false;
    }
    if (this.encoder && this.configured) {
      return true;
    }
    if (!OpusWebCodecsUpstreamEncoder.isSupported()) {
      console.warn('[WaveLink Opus uplink] 当前环境不支持 WebCodecs AudioEncoder');
      return false;
    }
    const head = buildOpusHeadMono48k();
    const description = head.slice().buffer as ArrayBuffer;
    const config: AudioEncoderConfig = {
      codec: 'opus',
      sampleRate: OPUS_UPLINK_SAMPLE_RATE,
      numberOfChannels: 1,
      bitrate: OPUS_UPLINK_BITRATE_BPS,
      description,
    };
    let supported = false;
    try {
      const check = await AudioEncoder.isConfigSupported(config);
      supported = !!check.supported;
    } catch {
      supported = false;
    }
    if (!supported) {
      console.warn('[WaveLink Opus uplink] AudioEncoder.isConfigSupported(opus) 未通过');
      return false;
    }

    this.encoder = new AudioEncoder({
      output: (chunk) => {
        try {
          const buf = new ArrayBuffer(chunk.byteLength);
          const u8 = new Uint8Array(buf);
          chunk.copyTo(u8);
          void Promise.resolve(this.onEncoded(buf));
        } catch (e) {
          console.error('[WaveLink Opus uplink] output 回调失败', e);
        }
      },
      error: (e) => {
        console.error('[WaveLink Opus uplink] AudioEncoder error', e);
      },
    });

    try {
      await this.encoder.configure(config);
      this.configured = true;
      this.timestampUs = 0;
      return true;
    } catch (e) {
      console.error('[WaveLink Opus uplink] AudioEncoder.configure 失败', e);
      try {
        this.encoder.close();
      } catch {
        /* ignore */
      }
      this.encoder = null;
      this.configured = false;
      return false;
    }
  }

  /** 喂入一段48 kHz 立体声交错 s16le（与现有 Worklet 上行 PCM 一致） */
  feedStereoS16le48k(audioData: ArrayBuffer): void {
    if (this.closed || !this.encoder || !this.configured) {
      return;
    }
    const mono = stereoS16leToMonoF32(audioData, getOpusUplinkInputGain());
    if (mono.length === 0) {
      return;
    }
    this.appendMono(mono);
    while (this.remainder.length >= OPUS_UPLINK_FRAME_SAMPLES) {
      this.encodeOneFrameSync();
    }
  }

  private appendMono(samples: Float32Array): void {
    if (samples.length === 0) {
      return;
    }
    const next = new Float32Array(this.remainder.length + samples.length);
    next.set(this.remainder);
    next.set(samples, this.remainder.length);
    this.remainder = next;
  }

  private encodeOneFrameSync(): void {
    if (!this.encoder || !this.configured || this.closed) {
      return;
    }
    const frame = this.remainder.subarray(0, OPUS_UPLINK_FRAME_SAMPLES);
    this.remainder = this.remainder.slice(OPUS_UPLINK_FRAME_SAMPLES);
    const plane = new Float32Array(frame);
    const audioData = new AudioData({
      format: 'f32-planar',
      sampleRate: OPUS_UPLINK_SAMPLE_RATE,
      numberOfFrames: OPUS_UPLINK_FRAME_SAMPLES,
      numberOfChannels: 1,
      timestamp: this.timestampUs,
      data: plane,
    } as AudioDataInit);
    this.timestampUs += (OPUS_UPLINK_FRAME_SAMPLES / OPUS_UPLINK_SAMPLE_RATE) * 1e6;
    try {
      this.encoder.encode(audioData);
    } finally {
      audioData.close();
    }
  }

  /** PTT 结束：尾帧补零到 20 ms，并 flush 编码器 */
  async flush(): Promise<void> {
    if (this.closed || !this.encoder || !this.configured) {
      this.remainder = new Float32Array(0);
      return;
    }
    try {
      if (this.remainder.length > 0) {
        const pad = new Float32Array(OPUS_UPLINK_FRAME_SAMPLES);
        pad.set(this.remainder.subarray(0, Math.min(this.remainder.length, OPUS_UPLINK_FRAME_SAMPLES)));
        this.remainder = new Float32Array(0);
        this.appendMono(pad);
      }
      while (this.remainder.length >= OPUS_UPLINK_FRAME_SAMPLES) {
        this.encodeOneFrameSync();
      }
      this.remainder = new Float32Array(0);
      await this.encoder.flush();
    } catch (e) {
      console.warn('[WaveLink Opus uplink] flush 失败', e);
    }
  }

  close(): void {
    this.closed = true;
    if (this.encoder && this.encoder.state !== 'closed') {
      try {
        this.encoder.close();
      } catch {
        /* ignore */
      }
    }
    this.encoder = null;
    this.configured = false;
    this.remainder = new Float32Array(0);
    this.timestampUs = 0;
  }

  isConfigured(): boolean {
    return !!this.encoder && this.configured && !this.closed;
  }
}
